import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { Box, Typography, TextField, Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom'; // Added for navigation

const PlatformSettings = () => {
  const { adminUser } = useContext(AuthContext);
  const [patientCommission, setPatientCommission] = useState('');
  const [doctorCommission, setDoctorCommission] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate(); // Added for navigation

  useEffect(() => {
    // Check if adminUser and accessToken exist
    if (!adminUser || !adminUser.accessToken) {
      console.log('No admin user or token found, redirecting to login');
      navigate('/admin-login');
      return;
    }

    const fetchSettings = async () => {
      try {
        const response = await api.get('/admin/platform-settings', {
          headers: { Authorization: `Bearer ${adminUser.accessToken}` }, // Updated to accessToken
        });
        setPatientCommission(response.data.patientCommission || '');
        setDoctorCommission(response.data.doctorCommission || '');
      } catch (error) {
        console.error('Error fetching settings:', error.response?.data || error.message);
        setSnackbar({ open: true, message: 'Failed to load settings', severity: 'error' });
      }
    };
    fetchSettings();
  }, [adminUser, navigate]);

  const handleSave = async () => {
    if (!adminUser || !adminUser.accessToken) {
      navigate('/admin-login');
      return;
    }
    if (!patientCommission || !doctorCommission || isNaN(patientCommission) || isNaN(doctorCommission)) {
      setSnackbar({ open: true, message: 'Please enter valid commission percentages', severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      await api.put(
        '/admin/platform-settings',
        { patientCommission: parseFloat(patientCommission), doctorCommission: parseFloat(doctorCommission) },
        { headers: { Authorization: `Bearer ${adminUser.accessToken}` } } // Updated to accessToken
      );
      setSnackbar({ open: true, message: 'Commission settings saved successfully', severity: 'success' });
    } catch (error) {
      console.error('Error saving settings:', error.response?.data || error.message);
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  if (!adminUser || !adminUser.accessToken) {
    return null; // Render nothing while redirecting
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Platform Settings</Typography>
      <Typography variant="h6" gutterBottom>Commission Rates</Typography>
      <TextField
        label="Patient Commission Percentage"
        value={patientCommission}
        onChange={(e) => setPatientCommission(e.target.value)}
        type="number"
        helperText="Percentage added to doctor's fee, paid by patient"
        sx={{ mb: 2, width: '300px' }}
        disabled={loading}
      />
      <TextField
        label="Doctor Commission Percentage"
        value={doctorCommission}
        onChange={(e) => setDoctorCommission(e.target.value)}
        type="number"
        helperText="Percentage deducted from doctor's fee"
        sx={{ mb: 2, width: '300px' }}
        disabled={loading}
      />
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : null}
      >
        Save
      </Button>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlatformSettings;